import { api } from '@/services/api/AxiosBaseService'

export interface SupportTeam {
  id: string
  name: string
  description: string | null
  autoAssign: boolean
  /** Palavras que direcionam o chamado para esta fila. */
  keywords: string[]
  status: string
  members: Array<{ id: string; name: string }>
}

export interface SupportLabel {
  id: string
  name: string
  color: string
  status: string
}

export interface SupportCanned {
  id: string
  shortcut: string
  title: string
  content: string
  /** Equipes que enxergam a resposta. Vazio = todas. */
  teams: Array<{ id: string; name: string }>
  teamIds: string[]
  status: string
}

export interface SupportInbox {
  id: string
  channel: 'APP' | 'WHATSAPP'
  slug: string
  name: string
  status: string
  defaultTeam: { id: string; name: string } | null
  phoneNumberId: string | null
}

/** Assinante encontrado na busca para vincular um contato do WhatsApp. */
export interface SubscriberHit {
  id: string
  name: string
  cpf: string
  phone: string
  status: string
}

/**
 * Cadastros da Central: equipes, etiquetas, respostas rápidas e caixas.
 * Cada rota exige a permissão do seu recurso (support.teams.*, etc.).
 */
export const SupportSettingsService = {
  // -- Equipes --
  async teams(): Promise<SupportTeam[]> {
    const r = await api.get<{ data: SupportTeam[] }>('/support/teams')
    return r.data.data
  },
  async saveTeam(payload: { id?: string; name: string; description?: string; autoAssign: boolean; keywords: string[]; memberIds: string[] }) {
    const body = {
      name: payload.name,
      description: payload.description || null,
      auto_assign: payload.autoAssign,
      keywords: payload.keywords,
      member_ids: payload.memberIds,
    }
    return payload.id
      ? api.put(`/support/teams/${payload.id}`, body)
      : api.post('/support/teams', body)
  },
  async deleteTeam(id: string) { await api.delete(`/support/teams/${id}`) },

  // -- Etiquetas --
  async labels(): Promise<SupportLabel[]> {
    const r = await api.get<{ data: SupportLabel[] }>('/support/labels')
    return r.data.data
  },
  async saveLabel(payload: { id?: string; name: string; color: string }) {
    return payload.id
      ? api.put(`/support/labels/${payload.id}`, payload)
      : api.post('/support/labels', payload)
  },
  async deleteLabel(id: string) { await api.delete(`/support/labels/${id}`) },

  // -- Respostas rápidas --
  async canned(): Promise<SupportCanned[]> {
    const r = await api.get<{ data: SupportCanned[] }>('/support/canned')
    return r.data.data
  },
  async saveCanned(payload: { id?: string; shortcut: string; title: string; content: string; teamIds: string[] }) {
    // Lista vazia = vale para todas as equipes (convenção do backend).
    const body = { shortcut: payload.shortcut, title: payload.title, content: payload.content, team_ids: payload.teamIds }
    return payload.id
      ? api.put(`/support/canned/${payload.id}`, body)
      : api.post('/support/canned', body)
  },
  async deleteCanned(id: string) { await api.delete(`/support/canned/${id}`) },

  // -- Caixas de entrada --
  async inboxes(): Promise<SupportInbox[]> {
    const r = await api.get<{ data: SupportInbox[] }>('/support/inboxes')
    return r.data.data
  },
  /**
   * Nome e fila padrão do canal. O número do WhatsApp NÃO passa por aqui —
   * ele pertence à tela da integração, para não haver dois donos do mesmo dado.
   */
  async saveInbox(id: string, payload: { name: string; defaultTeamId?: string | null }) {
    return api.put(`/support/inboxes/${id}`, {
      name: payload.name,
      default_team_id: payload.defaultTeamId || null,
    })
  },

  /** Cria um canal. Para WhatsApp, prefira a tela da integração — ela já
   * cria o canal com o número certo. */
  async createInbox(payload: { channel: 'APP' | 'WHATSAPP'; name: string; defaultTeamId?: string | null }) {
    return api.post('/support/inboxes', {
      channel: payload.channel,
      name: payload.name,
      default_team_id: payload.defaultTeamId || null,
    })
  },

  /**
   * Quem pode atender: só ADMINISTRADOR e COLABORADOR ativos. A regra é do
   * backend — a tela não filtra o cadastro geral de usuários por conta própria.
   */
  async agents(): Promise<Array<{ id: string; name: string; type: string }>> {
    const r = await api.get<{ data: Array<{ id: string; name: string; type: string }> }>('/support/agents')
    return r.data.data
  },

  /** Busca para vincular um contato sem cadastro a um assinante. */
  async searchSubscribers(q: string): Promise<SubscriberHit[]> {
    const r = await api.get<{ data: SubscriberHit[] }>('/support/subscriber-search', { params: { q } })
    return r.data.data
  },

  /** Indicadores do atendimento no período. */
  async metrics(from?: string, to?: string): Promise<SupportMetrics> {
    const r = await api.get<{ data: SupportMetrics }>('/support/metrics', { params: { from, to } })
    return r.data.data
  },
}

export interface SupportMetrics {
  period: { from: string; to: string }
  opened: number
  resolved: number
  waiting: number
  avgFirstResponseMinutes: number | null
  avgResolutionHours: number | null
  byChannel: Array<{ channel: string; total: number }>
  byAgent: Array<{ agent: string; total: number }>
}
